import React, { createElement, useEffect, useState } from 'react';
import './index.scss';
import { dealArray, dealOrderByBoList, dealFieldByList } from '../../../utils';
import { Chart, Point, Annotation, Axis, Coordinate, registerShape, registerAnimation, } from 'bizcharts';
import { previewDataByField } from '../../../api/common/index';
import ErrorBoundary from '../../common/errorBoundary';
import ErrorComponent from '../../common/errorComponent';
registerShape('point', 'pointer', {
    draw(cfg, container) {
        const group = container.addGroup();
        const center = this.parsePoint({ x: 0, y: 0 });
        const start = this.parsePoint({ x: 0, y: 0 });
        const detax = cfg.x - center.x;
        const detay = cfg.y - center.y;
        const oblique = Math.sqrt(Math.pow(detax, 2) + Math.pow(detay, 2));
        const smallOblique = oblique / 5;
        const theta = Math.atan2(detay, detax);
        const offsetx = smallOblique * Math.cos(theta);
        const offsety = smallOblique * Math.sin(theta);
        const line = group.addShape('line', {
            attrs: {
                x1: center.x - offsetx,
                y1: center.y - offsety,
                x2: cfg.x,
                y2: cfg.y,
                stroke: cfg.color,
                lineWidth: oblique / 10,
                lineCap: 'butt',
            },
        });
        group.addShape('circle', {
            attrs: {
                x: center.x,
                y: center.y,
                r: oblique / 10,
                stroke: cfg.color,
                lineWidth: oblique / 15,
                fill: '#fff',
            },
        });
        const preAngle = this.preAngle || 0;
        const angle1 = Math.atan((start.y - center.y) / (start.x - center.x));
        const angle = (Math.PI - 2 * angle1) * cfg.points[0].x;
        if (group.cfg.animable) {
            group.animate((ratio) => {
                group.resetMatrix();
                group.rotateAtPoint(center.x, center.y, preAngle + (angle - preAngle) * ratio);
            }, 300);
        }
        else {
            group.rotateAtPoint(center.x, center.y, angle);
        }
        this.preAngle = angle;
        return group;
    },
});
registerAnimation('cust-animation');
const IGaugeChart = (props, ref) => {
    const { dataSource, data, min, max, isSingel, color, colors, valueColor, pivotColor, hasTitle, titleText, } = props;
    const [idata, setIdata] = useState(0);
    const [error, setError] = useState(false);
    const [errmsg, setErrmsg] = useState(null);
    useEffect(() => {
        setError(false);
        try {
            if (dataSource?.main.length) {
                const fl = dataSource?.filter || [];
                const params = {
                    groupId: dataSource.dataset.id,
                    type: 'GaugeChart',
                    fieldList: [...dealFieldByList(dataSource.main)],
                    horizontalAxis: dealArray(dataSource.main).map((e) => ({
                        ...e,
                        aggregateType: e?.aggregateType || 'COUNT',
                    })),
                    orderByBoList: [...dealOrderByBoList(dataSource.main)],
                    customFilterVo: fl[0] === undefined
                        ? null
                        : {
                            logic: 'and',
                            filterVos: [dataSource?.filter[0] || null].filter((e) => e),
                        },
                };
                previewDataByField(params).then((res) => {
                    const fields = res?.data?.fields;
                    if (!fields) {
                        Message.error(res?.msg);
                        return;
                    }
                    const item = res?.data?.data[0];
                    const key = fields[0].fieldShortName;
                    setIdata(Number(item[key]));
                });
            }
            else {
                setIdata(data);
            }
        }
        catch (error) {
            console.log(error);
            setError(true);
            setErrmsg('初始化数据出错,请在数据源面板重新配置字段或更新组件');
        }
    }, [dataSource]);
    return (createElement(ErrorBoundary, null, error ? (createElement(ErrorComponent, { ref: ref, errmsg: errmsg })) : (createElement("div", { ref: ref, className: "base_bg" },
        hasTitle ? (createElement("div", { className: "datasettitle" }, titleText || dataSource?.dataset?.name)) : null,
        createElement(Chart, { forceUpdate: true, height: 400, data: [{ value: idata || 16 }], scale: {
                value: {
                    min: min || 0,
                    max: max || data * 1.1,
                    tickInterval: props.tickInterval || 10,
                    formatter: (v) => v,
                },
            }, autoFit: true },
            createElement(Coordinate, { type: "polar", radius: props.radius || 0.9, startAngle: (-12 / 10) * Math.PI, endAngle: (2 / 10) * Math.PI }),
            createElement(Axis, { name: "1" }),
            createElement(Axis, Object.assign({ name: "value", line: null, label: {
                    offset: -36 - (props.innerRadius || 0),
                    style: {
                        fontSize: 18,
                        textAlign: 'center',
                        textBaseline: 'middle',
                        fill: props.showTick ? 'a' : 'transparent',
                    },
                } }, (props.showTick
                ? {
                    subTickLine: {
                        count: 4,
                        length: -15 - (props.innerRadius || 0),
                    },
                    tickLine: {
                        length: -24 - (props.innerRadius || 0),
                    },
                }
                : {}), { grid: null })),
            createElement(Point, { position: "value*1", color: props.pivot ? pivotColor || '#3152FF' : 'transparent', shape: "pointer" }),
            createElement(Annotation.Arc, { start: [0, 1], end: [max || data * 1.1, max || data * 1.1], style: {
                    stroke: '#CBCBCB',
                    lineWidth: props.rangeSize || 18,
                    lineDash: null,
                    lineCap: props.rounded ? 'round' : '',
                } }),
            isSingel || colors.length === 0 ? (createElement(Annotation.Arc, { start: [0, 1], end: [idata, max || data * 1.1], style: {
                    stroke: color,
                    lineWidth: props.rangeSize || 18,
                    lineCap: props.rounded ? 'round' : '',
                } })) : (colors.map((e) => (createElement(Annotation.Arc, { start: [Math.min(((max || data * 1.1) / 100) * e.start, idata), 1], end: [Math.min(((max || data * 1.1) / 100) * e.end, idata), 1], style: {
                    stroke: e.color,
                    lineWidth: props.rangeSize || 18,
                    lineCap: props.rounded && ((max || data * 1.1) / 100) * e.start < idata ? 'round' : '',
                } })))),
            createElement(Annotation.Text, { position: [0, 0], content: idata, style: {
                    textAlign: 'center',
                    fontSize: 56,
                    fill: props.openAssistvalue ? valueColor || '#000' : 'transparent',
                    fontWeight: 500,
                }, offsetY: props.position === 'bottom' ? 140 * props.radius : 0 }))))));
};
export default React.forwardRef(IGaugeChart);
